/* sugar:assets/gift-picker.js@2.2.0 */
/*
 * gift-picker — one engine for Gift Picker and Cart Gift Picker.
 *
 * A root is any [data-sugar-gift-picker]; its data-gift-picker-config says which mode it runs in:
 *   page — picks are remembered on the page and ride along with whichever buy button submits,
 *          through SugarCart.extras (the root carries data-cart-extras and exposes sugarExtras).
 *          Inside an Offer the root only hands lines over while that offer is the selected one.
 *   cart — a pick adds the gift line straight away. The root unlocks on its own threshold or on a
 *          progress bar goal, and takes its gifts back out when the cart drops under it.
 *
 * Every line this engine adds carries _gift_picker = the block id, which is how a root finds its
 * own lines again after a reload, and how the cart total is measured without them.
 *
 * A page picker tells the rest of the page what was picked (document event sugar:gift-pick) and
 * remembers it for the visit. Two things follow it: a picker set to show "The shopper's pick"
 * (config mirror: one card at a time, nothing to add, the picked image flies into it), and
 * [gift]#1: …|#2: …[/gift] wording (snippets/tier-text.liquid renders [data-gift-text] groups).
 */
(function () {
  'use strict';
  if (window.SugarGiftPicker) return;

  const ROOT = '[data-sugar-gift-picker]';
  const CHOICE = '[data-gift-choice]';
  const PROP = '_gift_picker';
  const FOR_PROP = '_gift_for_product';
  // Lines the theme's gift blocks add; by default none of them counts toward a threshold
  const GIFT_PROPS = [PROP, '_gift_list', '_gift_unlock', '_offer_gift'];

  function isGiftLine(item) {
    const p = item && item.properties;
    return !!p && GIFT_PROPS.some((key) => p[key] !== undefined && p[key] !== null && p[key] !== '');
  }
  const int = (value, fallback = 0) => {
    const n = parseInt(value, 10);
    return Number.isFinite(n) ? n : fallback;
  };

  // trim: a whole amount drops its .00 (the cart drawer's "Hide .00 on whole amounts").
  function formatMoney(cents, format, trim) {
    const whole = !!trim && Math.round(Number(cents)) % 100 === 0;
    if (window.SugarPricing && typeof window.SugarPricing.formatMoney === 'function') {
      return window.SugarPricing.formatMoney(cents, format, whole);
    }
    return (format || '${{amount}}').replace(/\{\{\s*amount\w*\s*\}\}/, (int(cents) / 100).toFixed(whole ? 0 : 2));
  }

  function sectionIds() {
    if (typeof window.sugarCartSectionIds === 'function') return window.sugarCartSectionIds();
    return ['sugar-cart-drawer', 'cart-icon-bubble'];
  }

  async function postAdd(items) {
    const res = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items, sections: sectionIds(), sections_url: window.location.pathname }),
    });
    return res.json();
  }

  async function changeLine(key) {
    const payload = { id: key, quantity: 0, sections: sectionIds(), sections_url: window.location.pathname };
    if (window.SugarCart && typeof window.SugarCart.change === 'function') return window.SugarCart.change(payload);
    const res = await fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(payload),
    });
    return res.json();
  }

  async function announce(result, source) {
    const cartData = window.SugarCart ? await window.SugarCart.get({ fresh: true }) : null;
    if (cartData && typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      publish(PUB_SUB_EVENTS.cartUpdate, { source, cartData, sections: result.sections });
    }
    document.dispatchEvent(new CustomEvent('cart:update', { detail: result }));
    return cartData;
  }

  function isCart(value) {
    return !!(value && Array.isArray(value.items) && typeof value.item_count === 'number');
  }

  function cartOf(detail) {
    if (isCart(detail)) return detail;
    if (detail && isCart(detail.cart)) return detail.cart;
    if (detail && isCart(detail.cartData)) return detail.cartData;
    return null;
  }

  /* ------------------------------------------------------------ the pick, page-wide */

  const REDUCE = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const STORE = '_sw_gp_pick_';
  const GIFT_TEXT = '[data-gift-text]';
  let lastPick = null; // { blockId, productId, variantId, position }

  const storeGet = (key) => {
    try {
      return window.sessionStorage.getItem(STORE + key);
    } catch (e) {
      return null;
    }
  };
  const storeSet = (key, value) => {
    try {
      if (value) window.sessionStorage.setItem(STORE + key, value);
      else window.sessionStorage.removeItem(STORE + key);
    } catch (e) {
      /* private mode: the pick just isn't remembered */
    }
  };

  // [gift] wording: each group holds one entry per gift, keyed by position (`2` or `#2`). The
  // entry for the pick shows; with no pick, the entry that rendered visible stays.
  function showGiftText(scope, pick) {
    const host = scope && scope.nodeType === 1 ? scope : document;
    const groups = host.matches && host.matches(GIFT_TEXT) ? [host] : Array.from(host.querySelectorAll(GIFT_TEXT));
    if (!groups.length) return;
    const changes = [];
    groups.forEach((group) => {
      const entries = Array.from(group.children).filter((el) => el.hasAttribute('data-gift'));
      if (!entries.length) return;
      if (group.dataset.giftDefault === undefined) {
        const visible = entries.findIndex((el) => !el.hidden);
        group.dataset.giftDefault = String(Math.max(0, visible));
      }
      let match = null;
      if (pick && pick.position) {
        match = entries.find((el) => {
          const key = (el.getAttribute('data-gift') || '').replace('#', '').trim();
          return key !== '' && int(key, -1) === pick.position;
        });
      }
      if (!match) match = entries[int(group.dataset.giftDefault, 0)] || entries[0];
      entries.forEach((el) => {
        const hide = el !== match;
        if (el.hidden !== hide) changes.push([el, hide]);
      });
    });
    changes.forEach(([el, hide]) => {
      el.hidden = hide;
    });
  }

  // Send the picked image across to a mirror card. A copy under position:fixed travels, so
  // nothing in either layout moves; the mirror's own image fades in as the copy lands.
  function flyTo(source, card) {
    const target = card.querySelector('.sugar-gift-choice__image');
    if (REDUCE || !source || !target) return;
    const a = source.getBoundingClientRect();
    const b = target.getBoundingClientRect();
    if (!a.width || !b.width || b.bottom < 0 || b.top > window.innerHeight) return;
    const copy = source.cloneNode(true);
    copy.removeAttribute('loading');
    copy.style.cssText = 'position:fixed;z-index:60;margin:0;pointer-events:none;object-fit:contain;' +
      'left:' + a.left + 'px;top:' + a.top + 'px;width:' + a.width + 'px;height:' + a.height + 'px;' +
      'transition:transform .48s cubic-bezier(.22,.61,.36,1);';
    document.body.appendChild(copy);
    card.classList.add('is-arriving');
    const scale = Math.min(b.width / a.width, b.height / a.height);
    const dx = b.left + b.width / 2 - (a.left + a.width / 2);
    const dy = b.top + b.height / 2 - (a.top + a.height / 2);
    requestAnimationFrame(() => {
      copy.style.transform = 'translate(' + dx + 'px,' + dy + 'px) scale(' + scale + ')';
    });
    let landed = false;
    const land = () => {
      if (landed) return;
      landed = true;
      card.classList.remove('is-arriving');
      card.classList.add('is-landed');
      copy.style.transition = 'opacity .25s ease';
      copy.style.opacity = '0';
      setTimeout(() => {
        copy.remove();
        card.classList.remove('is-landed');
      }, 420);
    };
    copy.addEventListener('transitionend', (e) => {
      if (e.propertyName === 'transform') land();
    });
    setTimeout(land, 560);
  }

  class GiftPicker {
    constructor(root) {
      this.root = root;
      root.sugarGiftPicker = this;
      try {
        this.cfg = JSON.parse(root.dataset.giftPickerConfig || '{}');
      } catch (e) {
        this.cfg = {};
      }
      this.cfg.limit = Math.max(1, int(this.cfg.limit, 1));
      this.page = this.cfg.mode !== 'cart';
      this.mirror = this.page && this.cfg.mirror === true;
      this.order = [];
      this.announced = null;
      this.byShopper = false;
      this.handlers = [];
      this.busy = false;
      this.dirty = false;
      this.destroyed = false;
      this.cart = null;
      this.state = null;
      this.expanded = false;
      this.lastTagged = -1;
      this.head = root.querySelector('[data-gp-heading]');
      this.tplUnlocked = root.dataset.gpHeading || '';
      this.tplLocked = root.dataset.gpHeadingLocked || '';
      this.init();
    }

    /* ------------------------------------------------------------ setup */

    init() {
      if (this.mirror) {
        this.initMirror();
        return;
      }
      this.fillWording();
      if (this.cfg.control === 'tap') {
        this.choices().forEach((choice) => {
          choice.setAttribute('role', 'button');
          choice.setAttribute('tabindex', choice.dataset.available === 'false' ? '-1' : '0');
          choice.setAttribute('aria-pressed', 'false');
        });
      }
      this.bind();
      if (this.page) this.initPage();
      else this.initCart();
    }

    // The parent's wording is rendered into each card by Liquid; a render context that can't
    // reach the parent leaves the slots empty, and the config fills them here.
    fillWording() {
      const between = (this.cfg.betweenText || '').replace(/<[^>]*>/g, '').trim();
      this.choices().forEach((choice) => {
        // The price follows the parent's money format too (its currency code setting)
        const priceEl = choice.querySelector('[data-gc-price]');
        const template = choice.dataset.priceTemplate;
        if (priceEl && template && choice.dataset.price !== undefined && this.cfg.moneyFormat) {
          const html = template
            .replace(/\[price\]/g, formatMoney(choice.dataset.price, this.cfg.moneyFormat, this.cfg.hideCents))
            .replace(/\[compare_price\]/g, formatMoney(choice.dataset.compare || choice.dataset.price, this.cfg.moneyFormat, this.cfg.hideCents));
          if (priceEl.innerHTML !== html) priceEl.innerHTML = html;
        }
        const add = choice.querySelector('.sugar-gift-choice__button-add');
        const added = choice.querySelector('.sugar-gift-choice__button-added');
        // The config is authoritative: Liquid may have rendered only the plain default
        if (add && this.cfg.addText && add.innerHTML !== this.cfg.addText) add.innerHTML = this.cfg.addText;
        if (added && this.cfg.addedText && added.innerHTML !== this.cfg.addedText) added.innerHTML = this.cfg.addedText;
        if (between) {
          let span = choice.querySelector('.sugar-gift-choice__between');
          if (!span) {
            span = document.createElement('span');
            span.className = 'sugar-gift-choice__between';
            span.setAttribute('aria-hidden', 'true');
            choice.insertBefore(span, choice.firstChild);
          }
          if (span.innerHTML !== this.cfg.betweenText) span.innerHTML = this.cfg.betweenText;
        }
      });
    }

    destroy() {
      this.destroyed = true;
      this.handlers.forEach(([target, type, fn, opts]) => target.removeEventListener(type, fn, opts));
      this.handlers = [];
      if (typeof this.unsubscribe === 'function') this.unsubscribe();
      delete this.root.sugarGiftPicker;
      delete this.root.sugarExtras;
    }

    listen(target, type, fn, opts) {
      target.addEventListener(type, fn, opts);
      this.handlers.push([target, type, fn, opts]);
    }

    choices() {
      return Array.from(this.root.querySelectorAll(CHOICE));
    }

    offerEl() {
      return this.root.closest('.offer');
    }

    bind() {
      this.listen(this.root, 'click', (e) => {
        const change = e.target.closest('[data-gp-change]');
        if (change && this.root.contains(change)) {
          e.preventDefault();
          e.stopPropagation();
          this.expanded = true;
          this.paint();
          return;
        }
        const choice = e.target.closest(CHOICE);
        if (!choice || !this.root.contains(choice)) return;
        if (e.target.closest('select, a, option')) return;
        const button = e.target.closest('.sugar-gift-choice__button');
        if (this.cfg.control === 'button' && !button) return;
        e.preventDefault();
        e.stopPropagation();
        this.toggle(choice);
      });

      this.listen(this.root, 'keydown', (e) => {
        if (e.key !== 'Enter' && e.key !== ' ') return;
        const choice = e.target.closest(CHOICE);
        if (!choice || e.target !== choice) return;
        e.preventDefault();
        e.stopPropagation();
        this.toggle(choice);
      });

      this.listen(this.root, 'change', (e) => {
        const select = e.target.closest('.sugar-gift-choice__select');
        if (!select) return;
        const choice = select.closest(CHOICE);
        if (choice) this.switchVariant(choice, select);
      });

      this.listen(document, 'cart:update', (e) => this.onCartUpdate(cartOf(e.detail)));
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        this.unsubscribe = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => this.onCartUpdate(cartOf(event)));
      }
    }

    onCartUpdate(cart) {
      if (this.destroyed) return;
      if (cart) {
        this.cart = cart;
        this.reconcile();
      } else {
        this.refresh();
      }
    }

    async refresh() {
      if (!window.SugarCart) return;
      try {
        this.cart = await window.SugarCart.get({ fresh: true });
        this.reconcile();
      } catch (e) {
        /* the next cart event repaints */
      }
    }

    /* ------------------------------------------------------------ shared state */

    taggedLines(cart = this.cart) {
      if (!cart || !Array.isArray(cart.items)) return [];
      return cart.items.filter((item) => item.properties && item.properties[PROP] === this.cfg.blockId);
    }

    inCart(variantId) {
      return this.taggedLines().some((item) => String(item.variant_id) === String(variantId));
    }

    switchVariant(choice, select) {
      const option = select.selectedOptions && select.selectedOptions[0];
      if (!option) return;
      const previous = choice.dataset.variantId;
      choice.dataset.variantId = option.value;
      const priceEl = choice.querySelector('[data-gc-price]');
      const template = choice.dataset.priceTemplate;
      if (priceEl && template) {
        const format = this.cfg.moneyFormat;
        priceEl.innerHTML = template
          .replace(/\[price\]/g, formatMoney(option.dataset.price, format, this.cfg.hideCents))
          .replace(/\[compare_price\]/g, formatMoney(option.dataset.compare || option.dataset.price, format, this.cfg.hideCents));
      }
      const img = choice.querySelector('.sugar-gift-choice__image');
      if (img && option.dataset.image && img.tagName === 'IMG') {
        img.removeAttribute('srcset');
        img.src = option.dataset.image;
      }
      if (this.page) {
        const index = this.order.indexOf(String(previous));
        if (index > -1) this.order[index] = String(option.value);
        this.paint();
        return;
      }
      // A gift already in the cart follows its new variant
      if (this.inCart(previous)) this.swap(previous, choice);
    }

    /* ------------------------------------------------------------ page mode */

    initPage() {
      this.root.sugarExtras = {
        collect: () => this.collect(),
        commit: () => this.commit(),
      };
      const remembered = storeGet(this.cfg.blockId);
      const kept = remembered && this.choices().find((choice) => String(choice.dataset.variantId) === remembered && choice.dataset.available !== 'false');
      if (kept) {
        this.order.push(remembered);
      } else if (this.cfg.preselectFirst) {
        const first = this.choices().find((choice) => choice.dataset.available !== 'false');
        if (first) this.order.push(String(first.dataset.variantId));
      }
      this.paint();
      this.refresh();
    }

    /* ------------------------------------------------------------ the pick, page-wide */

    // The card the shopper has picked (the latest one when several can be), or none
    currentPick() {
      const choices = this.choices();
      const inCart = new Set(this.taggedLines().map((item) => String(item.variant_id)));
      let variantId = this.order.length ? this.order[this.order.length - 1] : null;
      if (!variantId) {
        const held = choices.find((choice) => inCart.has(String(choice.dataset.variantId)));
        variantId = held ? String(held.dataset.variantId) : null;
      }
      if (!variantId) return null;
      const index = choices.findIndex((choice) => String(choice.dataset.variantId) === variantId);
      if (index < 0) return null;
      return { blockId: this.cfg.blockId, productId: this.cfg.productId || null, variantId, position: index + 1, choice: choices[index] };
    }

    // Tell the page, once per change: mirrors and [gift] wording follow, and the visit remembers it
    tell() {
      const pick = this.currentPick();
      const key = pick ? pick.variantId + ':' + pick.position : '';
      if (key === this.announced) return;
      this.announced = key;
      if (this.order.length || !pick) storeSet(this.cfg.blockId, this.order.length ? this.order[this.order.length - 1] : '');
      const detail = pick
        ? { blockId: pick.blockId, productId: pick.productId, variantId: pick.variantId, position: pick.position, byShopper: this.byShopper }
        : { blockId: this.cfg.blockId, productId: this.cfg.productId || null, variantId: null, position: null, byShopper: this.byShopper };
      lastPick = pick ? detail : null;
      showGiftText(document, lastPick);
      document.dispatchEvent(new CustomEvent('sugar:gift-pick', { detail: Object.assign({ image: pick ? pick.choice.querySelector('.sugar-gift-choice__image') : null }, detail) }));
    }

    /* ------------------------------------------------------------ mirror */

    initMirror() {
      this.fillWording();
      this.listen(document, 'sugar:gift-pick', (e) => this.follow(e.detail || {}));
      if (lastPick) this.follow(lastPick);
    }

    follow(pick) {
      const own = this.cfg.productId;
      if (own && pick.productId && String(own) !== String(pick.productId)) return;
      const choices = this.choices();
      if (!choices.length) return;
      let card = null;
      if (pick.variantId) card = choices.find((choice) => String(choice.dataset.variantId) === String(pick.variantId));
      if (!card && pick.position) card = choices[pick.position - 1] || null;
      if (!card) card = choices[0];
      const was = choices.find((choice) => choice.classList.contains('is-shown'));
      choices.forEach((choice) => {
        const on = choice === card;
        if (choice.classList.contains('is-shown') !== on) choice.classList.toggle('is-shown', on);
      });
      if (!this.root.classList.contains('is-live')) this.root.classList.add('is-live');
      if (pick.byShopper && pick.image && was && was !== card) flyTo(pick.image, card);
    }

    collect() {
      const offer = this.offerEl();
      if (offer && !offer.classList.contains('is-selected')) return [];
      const lines = [];
      const room = Math.max(0, this.cfg.limit - this.taggedLines().length);
      this.order.slice(-room).forEach((variantId) => {
        if (this.inCart(variantId)) return;
        const id = int(variantId);
        if (!id) return;
        const properties = { [PROP]: this.cfg.blockId };
        if (this.cfg.productId) properties[FOR_PROP] = String(this.cfg.productId);
        lines.push({ id, quantity: 1, properties });
      });
      return lines;
    }

    commit() {
      // The buy button calls this once the lines are in the cart. Not every buy path publishes a
      // cart event afterwards (a page with no drawer doesn't), so the repaint is asked for here.
      this.order = [];
      this.refresh();
    }

    /* ------------------------------------------------------------ cart mode */

    initCart() {
      this.state = this.root.dataset.state || null;
      this.paint();
      this.refresh();
    }

    goalEl() {
      const scope = this.root.closest('sugar-cart-drawer, sugar-cart-page, .shopify-section') || document;
      return scope.querySelector('sugar-cart-progress[data-goal-count]') || document.querySelector('sugar-cart-progress[data-goal-count]');
    }

    evaluate(cart) {
      const unlock = this.cfg.unlock || {};
      const hasSub = cart.items.some((item) => item.selling_plan_allocation);
      const fromGoal = typeof unlock.source === 'string' && unlock.source.indexOf('goal_') === 0;
      // Own threshold: gift lines are left out unless the merchant counts them, and this block's
      // own gifts never count. A progress goal measures exactly what the bar measures.
      const isExcluded = (item) => (unlock.giftsCount ? item.properties && item.properties[PROP] === this.cfg.blockId : isGiftLine(item));
      const excluded = fromGoal ? [] : cart.items.filter(isExcluded);
      const ownTotal = excluded.reduce((sum, item) => sum + int(item.final_line_price), 0);
      const ownQty = excluded.reduce((sum, item) => sum + int(item.quantity), 0);
      let type = unlock.type || 'total';
      let target = int(unlock.target);
      let afterDiscounts = unlock.afterDiscounts !== false;
      let subUnlocks = !!unlock.subscriptionUnlocks;

      if (fromGoal) {
        const n = unlock.source.slice(5);
        const el = this.goalEl();
        // getAttribute, not dataset: a digit in the name (data-goal-2-type) breaks dataset's camelCase
        const goalType = el && el.getAttribute('data-goal-' + n + '-type');
        if (!el || !goalType) return { known: false, reached: false, remainingAmount: 0, remainingQty: 0 };
        type = goalType === 'quantity' ? 'quantity' : 'total';
        target = int(el.getAttribute('data-goal-' + n + '-target'));
        afterDiscounts = el.getAttribute('data-after-discounts') !== 'false';
        subUnlocks = el.getAttribute('data-goal-' + n + '-autocomplete-sub') === 'true';
      }

      const measured = Math.max(0, int(afterDiscounts || cart.items_subtotal_price == null ? cart.total_price : cart.items_subtotal_price) - ownTotal);
      const count = Math.max(0, int(cart.item_count) - ownQty);
      let reached = false;
      let remainingAmount = 0;
      let remainingQty = 0;
      if (type === 'quantity') {
        reached = count >= target;
        remainingQty = Math.max(0, target - count);
      } else if (type === 'products') {
        const ids = (unlock.products || []).map(String);
        reached = cart.items.some((item) => !isExcluded(item) && ids.includes(String(item.product_id)));
      } else {
        reached = measured >= target;
        remainingAmount = Math.max(0, target - measured);
      }
      if (subUnlocks && hasSub) reached = true;
      return { known: true, reached, remainingAmount, remainingQty };
    }

    autoKey() {
      return '_sw_gp_auto_' + this.cfg.blockId;
    }

    autoDeclined() {
      try {
        return sessionStorage.getItem(this.autoKey()) === '1';
      } catch (e) {
        return false;
      }
    }

    markAutoDeclined() {
      try {
        sessionStorage.setItem(this.autoKey(), '1');
      } catch (e) {
        /* private mode */
      }
    }

    setState(state) {
      this.state = state;
      this.root.dataset.state = state;
    }

    async reconcile() {
      if (this.destroyed || !this.cart) return;
      if (this.busy) {
        this.dirty = true;
        return;
      }
      const cart = this.cart;
      const tagged = this.taggedLines(cart);

      if (this.page) {
        // A gift whose product left the cart leaves with it
        const productIds = new Set(cart.items.map((item) => String(item.product_id)));
        const orphans = tagged.filter((item) => item.properties[FOR_PROP] && !productIds.has(String(item.properties[FOR_PROP])));
        if (orphans.length) {
          await this.removeLines(orphans);
          return;
        }
        this.order = this.order.filter((variantId) => !this.inCart(variantId));
        this.paint();
        return;
      }

      const result = this.evaluate(cart);
      this.eval = result;
      if (!result.known) {
        this.root.dataset.reason = 'no-goal';
        this.setState('hidden');
        this.paint();
        return;
      }
      delete this.root.dataset.reason;
      if (!result.reached) {
        if (tagged.length) {
          await this.removeLines(tagged);
          return;
        }
        this.setState(this.cfg.beforeUnlock === 'locked' ? 'locked' : 'hidden');
        this.paint();
        return;
      }
      if (tagged.length > this.cfg.limit) {
        await this.removeLines(tagged.slice(0, tagged.length - this.cfg.limit));
        return;
      }
      // A shopper who took the lone auto-added gift out isn't handed it again this session
      if (this.state === 'unlocked' && this.lastTagged > 0 && tagged.length === 0) this.markAutoDeclined();
      this.lastTagged = tagged.length;
      this.setState('unlocked');
      if (this.cfg.autoPickSingle && !tagged.length && !this.autoDeclined()) {
        const available = this.choices().filter((choice) => choice.dataset.available !== 'false');
        if (available.length === 1) {
          await this.add(available[0]);
          return;
        }
      }
      this.paint();
    }

    /* ------------------------------------------------------------ picking */

    async toggle(choice) {
      const variantId = String(choice.dataset.variantId || '');
      if (!variantId || choice.dataset.available === 'false' || this.busy) return;

      if (this.page) {
        const index = this.order.indexOf(variantId);
        // One gift, preselected: the picker works like a radio button, a pick can be swapped
        // but not taken away, so the page never shows a gift the buy button wouldn't add
        if (index > -1 && this.cfg.limit === 1 && this.cfg.preselectFirst && this.order.length === 1) return;
        this.byShopper = true;
        if (index > -1) {
          this.order.splice(index, 1);
        } else if (this.inCart(variantId)) {
          await this.removeLines(this.taggedLines().filter((item) => String(item.variant_id) === variantId));
          return;
        } else {
          this.order.push(variantId);
          // Gifts already in the cart from an earlier buy count against the limit too
          const inCartCount = this.taggedLines().length;
          while (this.order.length > 1 && this.order.length + inCartCount > this.cfg.limit) this.order.shift();
          if (this.order.length + inCartCount > this.cfg.limit) {
            const oldest = this.taggedLines().slice(0, this.order.length + inCartCount - this.cfg.limit);
            this.paint();
            await this.removeLines(oldest);
            return;
          }
        }
        this.paint();
        // A pick inside an offer selects that offer, the way ticking its upsell does
        const offer = this.offerEl();
        if (offer && !offer.classList.contains('is-selected')) {
          const host = offer.closest('[data-sugar-offers]');
          if (host && host.sugarOffers && typeof host.sugarOffers.selectOffer === 'function') {
            host.sugarOffers.selectOffer(offer.dataset.blockId, int(offer.dataset.quantity, 1));
          }
        }
        return;
      }

      if (this.state !== 'unlocked') return;
      if (this.inCart(variantId)) {
        await this.removeLines(this.taggedLines().filter((item) => String(item.variant_id) === variantId));
      } else {
        await this.add(choice);
      }
    }

    lineFor(choice) {
      const id = int(choice.dataset.variantId);
      if (!id) return null;
      return { id, quantity: 1, properties: { [PROP]: this.cfg.blockId } };
    }

    async add(choice) {
      if (this.busy) return;
      this.busy = true;
      this.root.classList.add('is-busy');
      choice.classList.add('is-busy');
      try {
        const line = this.lineFor(choice);
        if (!line) return;
        const tagged = this.taggedLines();
        const overflow = tagged.length + 1 - this.cfg.limit;
        if (overflow > 0) await this.removeLines(tagged.slice(0, overflow), { silent: true, nested: true });
        const result = await postAdd([line]);
        if (result.status) {
          console.error('[Gift Picker] Cart error:', result.description);
          return;
        }
        this.expanded = false;
        await announce(result, 'cart-gift-picker');
      } catch (e) {
        console.error('[Gift Picker] Add error:', e);
      } finally {
        this.busy = false;
        this.root.classList.remove('is-busy');
        choice.classList.remove('is-busy');
        this.flush();
      }
    }

    async swap(previousVariantId, choice) {
      const lines = this.taggedLines().filter((item) => String(item.variant_id) === String(previousVariantId));
      await this.removeLines(lines, { silent: true });
      await this.add(choice);
    }

    async removeLines(lines, opts = {}) {
      if (!lines.length) return;
      const nested = !!opts.nested;
      if (!nested) {
        this.busy = true;
        this.root.classList.add('is-busy');
      }
      try {
        let result = null;
        for (const line of lines) {
          result = await changeLine(line.key);
          if (!result || result.status) {
            console.error('[Gift Picker] Cart error:', result && result.description);
            break;
          }
        }
        if (result && !result.status) {
          if (opts.silent) {
            if (isCart(result)) this.cart = result;
          } else {
            await announce(result, 'cart-gift-picker');
          }
        }
      } catch (e) {
        console.error('[Gift Picker] Remove error:', e);
      } finally {
        if (!nested) {
          this.busy = false;
          this.root.classList.remove('is-busy');
          this.flush();
        }
      }
    }

    // A cart event that arrived mid-request is honoured once the request settles
    flush() {
      if (!this.dirty) return;
      this.dirty = false;
      this.reconcile();
    }

    /* ------------------------------------------------------------ paint */

    fill(template, picked) {
      const result = this.eval || {};
      return template
        .replace(/\[limit\]/g, String(this.cfg.limit))
        .replace(/\[picked\]/g, String(picked))
        .replace(/\[amount\]/g, formatMoney(result.remainingAmount || 0, this.cfg.moneyFormat, this.cfg.hideCents))
        .replace(/\[quantity\]/g, String(result.remainingQty || 0));
    }

    paint() {
      const tagged = this.taggedLines();
      const inCart = new Set(tagged.map((item) => String(item.variant_id)));
      let picked = 0;
      const names = [];
      this.choices().forEach((choice) => {
        const variantId = String(choice.dataset.variantId);
        const on = this.page ? this.order.includes(variantId) || inCart.has(variantId) : inCart.has(variantId);
        choice.classList.toggle('is-picked', on);
        choice.classList.toggle('is-in-cart', inCart.has(variantId));
        if (this.cfg.control === 'tap') choice.setAttribute('aria-pressed', String(on));
        if (on) {
          picked += 1;
          names.push(choice.dataset.title || '');
        }
      });

      if (this.head) {
        const locked = this.state === 'locked';
        const template = locked && this.tplLocked ? this.tplLocked : this.tplUnlocked;
        this.head.innerHTML = this.fill(template, picked);
      }

      const collapse = !this.page && this.cfg.afterPick === 'collapse' && picked > 0 && !this.expanded && this.state === 'unlocked';
      this.root.classList.toggle('is-collapsed', collapse);
      const summary = this.root.querySelector('[data-gp-summary-text]');
      if (summary) {
        const text = (this.cfg.summaryText || '[gift]').replace(/\[gift\]/g, names.filter(Boolean).join(', '));
        summary.textContent = text;
      }
      if (this.page && !this.mirror) {
        this.tell();
        this.byShopper = false;
      }
    }
  }

  /* ------------------------------------------------------------ boot */

  function init(scope) {
    const host = scope && scope.nodeType === 1 ? scope : document;
    const roots = host.matches && host.matches(ROOT) ? [host] : Array.from(host.querySelectorAll(ROOT));
    roots.forEach((root) => {
      if (!root.sugarGiftPicker) new GiftPicker(root);
    });
  }

  function teardown(scope) {
    const host = scope && scope.nodeType === 1 ? scope : null;
    if (!host) return;
    const roots = host.matches(ROOT) ? [host] : Array.from(host.querySelectorAll(ROOT));
    roots.forEach((root) => root.sugarGiftPicker && root.sugarGiftPicker.destroy());
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init());
  else init();

  // Roots that arrive later (a drawer fallback re-render, a block added in the editor) and
  // roots that leave (a block removed, a section re-rendered) are picked up here.
  const observer = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      for (const node of mutation.removedNodes) {
        if (node.nodeType === 1) teardown(node);
      }
      for (const node of mutation.addedNodes) {
        if (node.nodeType !== 1) continue;
        if (node.matches(ROOT) || node.querySelector(ROOT)) init(node);
        if (lastPick && (node.matches(GIFT_TEXT) || node.querySelector(GIFT_TEXT))) showGiftText(node, lastPick);
      }
    }
  });
  observer.observe(document.documentElement, { childList: true, subtree: true });

  document.addEventListener('shopify:block:select', (e) => {
    const root = e.target && e.target.closest ? e.target.closest(ROOT) : null;
    if (root && root.sugarGiftPicker && root.sugarGiftPicker.cfg.afterPick === 'collapse') {
      root.sugarGiftPicker.expanded = true;
      root.sugarGiftPicker.paint();
    }
  });

  window.SugarGiftPicker = {
    init,
    pick() {
      return lastPick;
    },
    get(root) {
      return root && root.sugarGiftPicker ? root.sugarGiftPicker : null;
    },
  };
})();
